import React, { useState, useEffect } from "react";
import { toast } from 'react-toastify';
import { useParams } from "react-router-dom";
import ViewQuotation from "./view-quotation";
import CustomerDetails from "./customer-details";
import QuotationDetails from "./quotation-details";
import PaymentStep from "./payment-step";
import "./view-quotation.css"; // ensure styles loaded
import { verifyTravellerDob } from "../../api";
import { Decrypt } from "../../services/data-encription";

const steps = [
  { label: "Quotation Details", component: ViewQuotation },
  { label: "Proposer Details", component: CustomerDetails },
  { label: "Payment", component: PaymentStep },
];


const ViewQuotationStepper = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [quotationValid, setQuotationValid] = useState(true); // for validation
  const [quotationNumber, setQuotationNumber] = useState("");
  const [memberPremium, setMemberPremium] = useState(0);
  const [proposerName, setProposerName] = useState("");
  const [userId, setUserId] = useState("");

  // Handler for Next button, can add validation logic here
  const handleNext = (skipCheck = false) => {
    // If on first step, check validity unless explicitly skipped (child already validated & saved)
    if (!skipCheck && activeStep === 0 && !quotationValid) {
      toast.error("Please fill all required Quotation Details before proceeding.");
      return;
    }
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  // Handler for Back button
  const handleBack = () => {
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  // Pass a callback to ViewQuotation to update validation state

  const { id: encryptedId } = useParams();
  let decryptedData = null;
  try {
    // Use the shared Decrypt helper used elsewhere in the app
    const raw = Decrypt ? Decrypt(decodeURIComponent(encryptedId)) : null;
    // Decrypt might return a string or an object; normalize to object
    if (typeof raw === "string") {
      try {
        decryptedData = JSON.parse(raw);
      } catch (_) {
        decryptedData = raw;
      }
    } else {
      decryptedData = raw;
    }
  } catch (e) {
    console.error("Failed to decrypt quotation data in stepper", e);
  }
  const decryptedQuotationNumber = decryptedData?.quotation_number || "";
  const decryptedUserId = decryptedData?.user_id;
  console.log("Decrypted data in stepper:", decryptedData);
  // DOB verification modal state
  const [dobModalOpen, setDobModalOpen] = useState(true);
  const [dobInput, setDobInput] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [accessAllowed, setAccessAllowed] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const StepComponent = steps[activeStep].component;
  const progressPercent = steps.length > 1 ? (activeStep / (steps.length - 1)) * 100 : 0;
  const handleStepClick = (index) => {
    if (index === activeStep) return;
    if (index < activeStep) return setActiveStep(index);
    if (activeStep === 0 && !quotationValid) return;
    setActiveStep(index);
  };

  // Always use the correct quotation number for updates
  const currentQuotationNumber = quotationNumber || decryptedQuotationNumber;

  // ensure userId is propagated from decrypted payload if available
  useEffect(() => {
    if (decryptedUserId) {
      setUserId(decryptedUserId);
    }
  }, [decryptedUserId]);

  return (
    <div className="view-quotation-stepper-wrapper">
      {/* DOB modal: blocks access until verified */}
      {dobModalOpen && (
        <div className="vq-dob-modal">
          <div className="vq-dob-modal-inner">
            <h3>Confirm traveller DOB</h3>
            <p>Please enter traveller's Date of Birth to view quotation details.</p>
            <input
              type="date"
              className="vq-dob-input"
              value={dobInput}
              onChange={(e) => setDobInput(e.target.value)}
            />
            <div className="vq-dob-actions">
              <button className="vq-dob-cancel-btn" onClick={() => { setDobModalOpen(false); setAccessAllowed(false); }} disabled={verifying}>Cancel</button>
              <button
                onClick={async () => {
                  if (!dobInput) {
                    toast.error('Please enter DOB');
                    return;
                  }
                  setVerifying(true);
                  try {
                    const res = await verifyTravellerDob(currentQuotationNumber, dobInput);
                    // middlewareEncryption returns decrypted result in data.result
                    console.log('DOB verify response', res);
                    const valid = res?.data?.result ?? res?.data?.result ?? false;
                    if (valid) {
                      setAccessAllowed(true);
                      setDobModalOpen(false);
                      toast.success('DOB verified — access granted');
                    } else {
                      toast.error(res?.data?.result?.message || res?.data?.message || 'DOB mismatch. Access denied.');
                    }
                  } catch (err) {
                    console.error('DOB verify error', err);
                    toast.error('Verification failed.');
                  } finally {
                    setVerifying(false);
                  }
                }}
                className="vq-dob-verify-btn"
                disabled={verifying || !currentQuotationNumber}
              >
                {verifying ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </div>
        </div>
      )}
      {accessAllowed ? (
        loadError ? (
          <div className="vq-load-error">
            <div className="vq-load-icon">⚠️</div>
            <h3>Link expired or something went wrong.</h3>
            <p>Please check your link or contact support.</p>
            <div style={{ marginTop: 12 }}>
              <button onClick={() => {
                // clear error and reopen DOB modal to try again
                setLoadError(null);
                setDobModalOpen(true);
                // also reset access so user must re-verify
                setAccessAllowed(false);
              }} className="view-quotation-submit-button">Retry</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "12px" }}>
              <QuotationDetails
                quotationNumber={currentQuotationNumber}
                proposerName={proposerName}
                memberPremiumOverride={memberPremium}
              />
            </div>

            <div className="vq-stepper">
              <div className="vq-stepper-track">
                <div className="vq-stepper-progress" style={{ width: `${progressPercent}%` }} />
                {steps.map((step, idx) => {
                  const blockedForward = idx > activeStep && activeStep === 0 && !quotationValid;
                  const state = idx === activeStep ? "active" : idx < activeStep ? "done" : blockedForward ? "locked" : "";
                  return (
                    <button
                      type="button"
                      key={step.label}
                      className={`vq-stepper-node ${state}`}
                      onClick={() => handleStepClick(idx)}
                      aria-current={idx === activeStep ? "step" : undefined}
                      disabled={blockedForward}
                      title={step.label}
                    >
                      <span className="vq-stepper-index">{idx + 1}</span>
                      <span className="vq-stepper-label">{step.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="vq-stepper-content">
              {activeStep === 0 && (
                <StepComponent
                  onNext={handleNext}
                  isStepper={true}
                  setQuotationValid={setQuotationValid}
                  setUserId={setUserId}
                  onQuoteData={(q) => {
                    if (q) {
                      if (q.quotation_number) setQuotationNumber(q.quotation_number);
                      if (q.member_premium != null) setMemberPremium(q.member_premium);
                      else if (q.total_premium != null) setMemberPremium(q.total_premium);
                      const fullProposer = [q.proposer_title, q.proposer_first_name, q.proposer_last_name]
                        .filter(Boolean)
                        .join(" ");
                      if (fullProposer) setProposerName(fullProposer);
                      if (q.user_id) setUserId(q.user_id);
                    }
                  }}
                  onLoadError={setLoadError}
                />
              )}
              {activeStep === 1 && (
                <StepComponent
                  onBack={handleBack}
                  isStepper={true}
                  quotationId={currentQuotationNumber}
                  onFinish={() => setActiveStep(2)}
                />
              )}
              {activeStep === 2 && (
                <StepComponent
                  onBack={handleBack}
                  isStepper={true}
                  quotationNumber={currentQuotationNumber}
                  amount={memberPremium}
                  userId={userId}
                  onPaid={() => {
                    // Extend: navigate or show confirmation
                  }}
                />
              )}
            </div>
          </>
        )
      ) : null}
    </div>
  );
};

export default ViewQuotationStepper;